import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import {
    getAuth,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    signOut,
    onAuthStateChanged,
    sendPasswordResetEmail,
    updateProfile,
    User,
    GoogleAuthProvider,
    signInWithPopup,
    confirmPasswordReset
} from 'firebase/auth';
import apiClient from '../services/api/apiClient';

interface BackendUserData {
    id: string | null;
    name?: string;
    email?: string;
    timezone?: string | null;
    language?: string | null;
    subscription_plan?: string | null;
    is_active?: boolean | null;
    reset_token?: string | null;
    reset_token_expiry?: Date | string | null;
    onboarding_completed?: boolean | null;
    onboarding_completed_at?: Date | string | null;
    created_at?: Date | string | null;
    updated_at?: Date | string | null;
    [key: string]: any;
}

interface AuthContextType {
    user: User | null;
    userData: BackendUserData | null;
    loading: boolean;
    login: (email: string, password: string) => Promise<{ success: boolean; message?: string }>;
    register: (email: string, password: string, name: string) => Promise<{ success: boolean; message?: string }>;
    logout: () => Promise<void>;
    forgotPassword: (email: string) => Promise<{ success: boolean; message?: string; simulatedEmail?: string }>;
    resetPassword: (email: string, resetToken: string, newPassword: string) => Promise<{ success: boolean; message?: string }>;
    isAuthenticated: boolean;
    loginWithGoogle: () => Promise<{ success: boolean; message?: string }>;
    getUserIdToken: () => Promise<string | null>;
    refreshUserData: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [userData, setUserData] = useState<BackendUserData | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const auth = getAuth();

    console.log(userData);
    const loadUserData = useCallback(async () => {
        if (!auth.currentUser) {
            setUserData(null);
            return;
        }

        try {
            const response = await apiClient.get('/auth/initFrontend');
            const backendUser: BackendUserData | null = response?.data?.data ?? null;
            setUserData(backendUser);
        } catch (error) {
            console.error('Failed to fetch backend user data:', error);
            setUserData(null);
        }
    }, [auth]);

    // Handle Firebase Auth State Changes
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            setUser(currentUser);

            if (currentUser) {
                // Get JWT token and set it for apiClient
                try {
                    const token = await currentUser.getIdToken();
                    localStorage.setItem('token', token);
                    apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;

                    // Dispatch auth-change event for other components
                    window.dispatchEvent(new CustomEvent('auth-change', { detail: { userId: currentUser.uid } }));
                } catch (error) {
                    console.error('Error getting auth token:', error);
                }

                await loadUserData();
            } else {
                setUserData(null);
                localStorage.removeItem('token');
                localStorage.removeItem('selectedAccountId');
                delete apiClient.defaults.headers.common['Authorization'];
                window.dispatchEvent(new CustomEvent('auth-change', { detail: { userId: null } }));
            }
            setLoading(false);
        });

        return () => unsubscribe();
    }, [auth, loadUserData]);

    const login = async (email: string, password: string) => {
        try {
            await signInWithEmailAndPassword(auth, email, password);
            return { success: true };
        } catch (error: any) {
            console.error('Login error:', error);
            return {
                success: false,
                message: error.message || 'Login failed'
            };
        }
    };

    const register = async (email: string, password: string, name: string) => {
        try {
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            if (userCredential.user) {
                await updateProfile(userCredential.user, {
                    displayName: name
                });
            }

            return { success: true, message: 'Registration successful' };
        } catch (error: any) {
            console.error('Register error:', error);
            return {
                success: false,
                message: error.message || 'Registration failed'
            };
        }
    };

    const logout = async () => {
        try {
            await signOut(auth);
        } catch (error) {
            console.error('Logout error:', error);
        }
    };

    const forgotPassword = async (email: string) => {
        try {
            await sendPasswordResetEmail(auth, email);
            return {
                success: true,
                message: 'Password reset email sent',
                // Maintaining interface compatibility, though 'simulatedEmail' is not relevant for real Firebase
            };
        } catch (error: any) {
            console.error('Forgot password error:', error);
            return {
                success: false,
                message: error.message || 'Failed to send reset email'
            };
        }
    };

    // Note: Firebase handles password reset via a link that opens a specific handler action URL.
    // The frontend typically needs a dedicated route to handle the `mode=resetPassword` action from Firebase.
    // This function is kept for interface compatibility but might need to be adapted or removed depending on how the UI calls it.
    const resetPassword = async (email: string, resetToken: string, newPassword: string) => {
        try {
            await confirmPasswordReset(auth, resetToken, newPassword);

            return {
                success: true,
                message: 'Password successfully reset'
            };
        } catch (error: any) {
            console.error('Reset password error:', error);
            return {
                success: false,
                message: error.message || 'Failed to reset password'
            };
        }
    };

    const loginWithGoogle = async () => {
        const provider = new GoogleAuthProvider();
        try {
            await signInWithPopup(auth, provider);
            return { success: true };
        } catch (error: any) {
            console.error("Google login failed", error);
            return {
                success: false,
                message: error.message || "Google login failed"
            };
        }
    }

    const getUserIdToken = async () => {
        if (auth.currentUser) {
            return await auth.currentUser.getIdToken(true);
        }
        return null;
    };

    // Expose getUserIdToken to window for legacy support
    useEffect(() => {
        (window as any).__getUserIdToken = getUserIdToken;
        return () => {
            delete (window as any).__getUserIdToken;
        };
    }, []);

    const value = {
        user,
        userData,
        loading,
        login,
        register,
        logout,
        forgotPassword,
        resetPassword,
        isAuthenticated: !!user,
        loginWithGoogle,
        getUserIdToken,
        refreshUserData: loadUserData
    };

    return (
        <AuthContext.Provider value={value} >
            {children}
        </AuthContext.Provider>
    );
};

export { AuthContext };
